import React, { useEffect, useState } from 'react';
import { FiWifiOff, FiWifi, FiHardDrive, FiAlertTriangle, FiSmartphone } from 'react-icons/fi';
import { useOfflineManager } from '../hooks/useOfflineManager';
import { mobileOfflineManager } from '../services/mobileOfflineManager';
import { useLanguage } from '../context/LanguageContext';

const OfflineIndicator = ({ className = '', showOnlineStatus = false }) => {
  const { t } = useLanguage();
  const { isOnline, cacheStats } = useOfflineManager();
  const [mobileStatus, setMobileStatus] = useState(null);

  useEffect(() => {
    if (mobileOfflineManager.isMobile) {
      const updateMobileStatus = () => {
        setMobileStatus(mobileOfflineManager.getMobileStatus());
      };
      
      updateMobileStatus();
      
      // Update mobile status periodically
      const interval = setInterval(updateMobileStatus, 10000); // Every 10 seconds
      
      return () => clearInterval(interval);
    }
  }, []);

  // Show mobile-specific warnings
  const getMobileWarning = () => {
    if (!mobileStatus) return null;
    
    if (mobileStatus.storageQuota && mobileStatus.storageQuota.usagePercent > 85) {
      return {
        type: 'storage',
        message: t('offline.mobile.storageWarning'),
        color: 'orange'
      };
    }
    
    if (mobileStatus.isLowMemory) {
      return {
        type: 'memory',
        message: t('offline.mobile.lowMemory'),
        color: 'red'
      };
    }
    
    if (mobileStatus.connectionType.saveData) {
      return {
        type: 'datasaver',
        message: t('offline.mobile.dataSaverMode'),
        color: 'blue'
      };
    }
    
    if (mobileStatus.connectionType.effectiveType === 'slow-2g' || mobileStatus.connectionType.effectiveType === '2g') {
      return {
        type: 'slow',
        message: t('offline.mobile.slowConnection'),
        color: 'yellow'
      };
    }
    
    return null;
  };

  const mobileWarning = getMobileWarning();

  // Show online status if requested
  if (showOnlineStatus && isOnline && !mobileWarning) {
    return (
      <div className={`flex items-center gap-2 px-3 py-2 bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200 rounded-lg text-sm ${className}`}>
        <FiWifi className="w-4 h-4" />
        <span>Online</span>
        {mobileOfflineManager.isMobile && (
          <FiSmartphone className="w-3 h-3 opacity-60" />
        )}
      </div>
    );
  }

  // Show mobile warning if exists
  if (mobileWarning && isOnline) {
    const colorClasses = {
      orange: 'bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-200',
      red: 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200',
      blue: 'bg-blue-100 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200',
      yellow: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200'
    };

    return (
      <div className={`flex items-center gap-2 px-3 py-2 ${colorClasses[mobileWarning.color]} rounded-lg text-sm ${className}`}>
        <FiAlertTriangle className="w-4 h-4" />
        <span>{mobileWarning.message}</span>
        <FiSmartphone className="w-3 h-3 opacity-60" />
      </div>
    );
  }

  // Don't show anything when online (unless showOnlineStatus is true or there's a mobile warning)
  if (isOnline && !showOnlineStatus && !mobileWarning) {
    return null;
  }

  // Show offline indicator
  return (
    <div className={`flex items-center gap-2 px-3 py-2 bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-200 rounded-lg text-sm ${className}`}>
      <FiWifiOff className="w-4 h-4" />
      <span>Offline - Using cached content</span>
      {cacheStats && (
        <div className="flex items-center gap-1 ml-2 text-xs">
          <FiHardDrive className="w-3 h-3" />
          <span>{cacheStats.content?.totalItems || 0} items cached</span>
        </div>
      )}
      {mobileOfflineManager.isMobile && (
        <FiSmartphone className="w-3 h-3 opacity-60" />
      )}
    </div>
  );
};

export default OfflineIndicator;